import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
    List,
    Type,
    Calendar,
    AlignLeft,
    Hash,
    Tag,
    CheckSquare,
    DollarSign,
    Globe,
    X
} from 'lucide-react';

const COLUMN_TYPES = [
    { id: 'status', label: 'Lista desplegable', icon: List, description: 'Agrega contexto importante a cualquier elemento en una sola línea.' },
    { id: 'text', label: 'Texto', icon: Type, description: 'No todas las fechas son fechas límite. Marca las fechas importantes.' },
    { id: 'date', label: 'Fecha', icon: Calendar, description: 'Agrega detalles adicionales a cualquier elemento con formato personalizado.' },
    { id: 'long_text', label: 'Área de texto', icon: AlignLeft, description: 'Explica cualquier número asociado a una tarea.' },
    { id: 'number', label: 'Número', icon: Hash, description: 'Agrega a las tareas opciones numéricas.' },
    { id: 'tags', label: 'Etiquetas', icon: Tag, description: 'Marca sí o no, e incluso desencadena automatizaciones.' },
    { id: 'checkbox', label: 'Casilla de selección', icon: CheckSquare, description: 'Para tareas de sí/no.' },
    { id: 'money', label: 'Dinero', icon: DollarSign, description: 'Incluye información fiscal importante, presupuestos, costos.' },
    { id: 'url', label: 'Sitio web', icon: Globe, description: 'Descubre vínculos relevantes a recursos externos.' },
];

const AddColumnPopover = ({ onClose, onAddColumn, position }) => {
    const popoverRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (popoverRef.current && !popoverRef.current.contains(event.target)) {
                onClose();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [onClose]);

    if (!position) return null;

    // Calculate position to keep it on screen
    const style = {
        top: position.top + 10,
        left: position.left,
        maxHeight: '400px',
    };

    // Adjust if going off screen (simplified logic)
    if (window.innerWidth - position.left < 300) {
        style.left = 'auto';
        style.right = 20;
    }

    return createPortal(
        <div
            ref={popoverRef}
            style={style}
            className="fixed z-[60] w-72 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden flex flex-col animate-in fade-in zoom-in-95 duration-100"
        >
            <div className="p-3 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Añadir propiedad</span>
                <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                    <X size={14} />
                </button>
            </div>

            <div className="overflow-y-auto py-1">
                {COLUMN_TYPES.map((type) => (
                    <button
                        key={type.id}
                        onClick={() => onAddColumn(type)}
                        className="w-full text-left px-4 py-2 hover:bg-gray-50 flex items-start gap-3 transition-colors group"
                    >
                        <div className="mt-0.5 text-gray-400 group-hover:text-gray-600">
                            <type.icon size={16} />
                        </div>
                        <div>
                            <div className="text-sm font-medium text-gray-700 group-hover:text-gray-900">{type.label}</div>
                            <div className="text-[10px] text-gray-400 leading-tight mt-0.5">{type.description}</div>
                        </div>
                    </button>
                ))}
            </div>
        </div>,
        document.body
    );
};

export default AddColumnPopover;
